import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardHeader, Box, CardContent, Typography, TextField, Grid, Button, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { getPayments } from "../../State/Payment/Action";
import { FileDownload, PictureAsPdf, Print } from "@mui/icons-material";
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { getAllProduct } from "../../State/Product/Action";
import dayjs from "dayjs";

const Payments = () => {
  const dispatch = useDispatch();
  const { vendorPayments, vendorProducts } = useSelector((store) => store);
  const [orderDateFrom, setOrderDateFrom] = useState("");
  const [orderDateTo, setOrderDateTo] = useState("");
  const [paymentDateFrom, setPaymentDateFrom] = useState("");
  const [paymentDateTo, setPaymentDateTo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [productTitle, setProductTitle] = useState("");
  // Add this state for selection
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  useEffect(() => {
    dispatch(getPayments());
    dispatch(getAllProduct());
  }, [dispatch]);

  // Flatten the data structure to make it compatible with DataGrid
  const rows =
    vendorPayments.payments?.response?.map((payment, index) => ({
      id: `${payment.id}-${payment?.order?.orderItems?.map((item) => item?.product?.id).join("-")}-${index}`, // Unique ID for DataGrid
      orderId: payment.order.orderId,
      paymentId: payment.paymentId,
      orderDate: payment.order.orderDate,
      paymentDate: payment.paymentDate,
      transferDate: payment.transferDate,
      productTitle: payment?.order?.orderItems?.map((item) => item?.product?.title).join(", ") || "----",
      productName: payment?.order?.orderItems?.map((item) => item?.product?.productCategory?.name).join(", ") || "----",
      quantity: payment?.order?.totalItem || 0,
      price: payment?.order?.totalPrice?.toFixed(2) || 0,
      orderStatus: payment.order.orderStatus,
      deliveryDate: payment.order.deliveryDate,
      amount: payment?.order?.orderItems?.reduce(
        (sum, item) => sum + ((item?.variant?.vendorPrice || 0) * (item?.quantity || 0)),
        0
      ).toFixed(2),
      paymentStatus: payment.paymentStatus,
      adminPaymentStatus: payment.adminPaymentStatus
    })
    ) || [];

  const filteredRows = rows.filter((row) => {
    const orderDate = new Date(row.orderDate);
    const paymentDate = new Date(row.paymentDate);

    const isOrderDateInRange =
      (!orderDateFrom || orderDate >= new Date(orderDateFrom)) &&
      (!orderDateTo || orderDate <= new Date(orderDateTo));

    const isPaymentDateInRange =
      (!paymentDateFrom || paymentDate >= new Date(paymentDateFrom)) &&
      (!paymentDateTo || paymentDate <= new Date(paymentDateTo));

    const matchesSearch = (
      row.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.paymentId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.productTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.orderStatus.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.paymentStatus.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const isProductTitleMatch = productTitle
      ? row.productTitle.toLowerCase().trim() === productTitle.toLowerCase().trim()
      : true;
    return isOrderDateInRange && isPaymentDateInRange && matchesSearch && isProductTitleMatch;
  });

  const getExportData = () => {
    // If some rows are selected, filter them; else use all filteredRows
    if (selectedRowIds.length > 0) {
      return filteredRows.filter((row) => selectedRowIds.includes(row.id));
    }
    return filteredRows;
  };


  // Export, PDF, and Print Handlers
  const handleExportCSV = () => {
    const exportRows = getExportData(); // ✅ Call the function
    const csvContent = exportRows.map((row, index) => {
      return [
        index + 1,
        row.orderId,
        row.paymentId,
        row.orderDate ? dayjs(row.orderDate).format("DD MMM YYYY HH:mm") : "----",
        row.paymentDate ? dayjs(row.paymentDate).format("DD MMM YYYY HH:mm") : "----",
        row.productTitle,
        row.productName,
        row.quantity,
        row.amount,
        row.orderStatus,
        row.deliveryDate ? dayjs(row.deliveryDate).format("DD MMM YYYY") : "----",
        row.adminPaymentStatus === "TRANSFER" ? "RECIEVED" : "PENDING"
      ].join(',');
    }).join('\n');

    const headers = [
      'S.No.',
      'Order ID',
      'Payment ID',
      'Order Date',
      'Payment Date',
      'Product Titles',
      'Product Names',
      'Quantity',
      'Amount',
      'Order Status',
      'Delivery Date',
      'Payment Status'
    ].join(',');

    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'payments.csv';
    link.click();
  };

  const handleExportPDF = () => {
    const exportRows = getExportData(); // ✅ Call the function
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a2", // even larger than A3 to fit all columns
    });

    doc.setFontSize(18);
    doc.text("Payment List", 40, 30);

    const tableColumn = [
      'S.No.',
      'Order ID',
      'Payment ID',
      'Order Date',
      'Payment Date',
      'Product Titles',
      'Product Names',
      'Quantity',
      'Amount',
      'Order Status',
      'Delivery Date',
      'Payment Status'
    ];
    const tableRows = [];

    exportRows.forEach((row, index) => {
      const paymentData = [
        index + 1,
        row.orderId,
        row.paymentId,
        row.orderDate ? dayjs(row.orderDate).format("DD MMM YYYY HH:mm") : "----",
        row.paymentDate ? dayjs(row.paymentDate).format("DD MMM YYYY HH:mm") : "----",
        row.productTitle,
        row.productName,
        row.quantity,
        `₹${row.amount}`,
        row.orderStatus,
        row.deliveryDate ? dayjs(row.deliveryDate).format("DD MMM YYYY") : "----",
        row.adminPaymentStatus === "TRANSFER" ? "RECIEVED" : "PENDING"
      ];
      tableRows.push(paymentData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 50,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        valign: 'top',
      },
      columnStyles: {
        0: { cellWidth: 150 },
        1: { cellWidth: 150 },
        4: { cellWidth: 150 },
      },
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      bodyStyles: {
        halign: 'left'
      },
      margin: { top: 50, left: 10, right: 10, bottom: 20 },
      didDrawPage: (data) => {
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 10, doc.internal.pageSize.height - 10);
      },
    });
    doc.save('payments.pdf');
  };

  const handlePrint = () => {
    const exportRows = getExportData(); // ✅ Call the function
    const printWindow = window.open('', '_blank');
    const headers = [
      'S.No.',
      'Order ID',
      'Payment ID',
      'Order Date',
      'Payment Date',
      'Product Titles',
      'Product Names',
      'Quantity',
      'Amount',
      'Order Status',
      'Delivery Date',
      'Payment Status'
    ];

    const tableContent = exportRows.map((row, index) => [
      index + 1,
      row.orderId,
      row.paymentId,
      row.orderDate ? dayjs(row.orderDate).format("DD MMM YYYY HH:mm") : "----",
      row.paymentDate ? dayjs(row.paymentDate).format("DD MMM YYYY HH:mm") : "----",
      row.productTitle,
      row.productName,
      row.quantity,
      row.amount,
      row.orderStatus,
      row.deliveryDate ? dayjs(row.deliveryDate).format("DD MMM YYYY") : "----",
      row.adminPaymentStatus === "TRANSFER" ? "RECIEVED" : "PENDING"
    ]);

    const html = `
      <html>
        <head>
          <title>Payment List</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th {background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact; }
            tr:nth-child(even) { background-color: #f5f5f5; }
          </style>
        </head>
        <body>
          <h2>Payment List</h2>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${tableContent.map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  // Define columns
  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "orderId", headerName: "Order ID", width: 120 },
    { field: "paymentId", headerName: "Payment ID", width: 120 },
    { field: "orderDate", headerName: "Order Date", width: 150, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----", },
    { field: "transferDate", headerName: "Transfer Date", width: 150, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----", },
    { field: "productTitle", headerName: "Product Titles", width: 120 },
    { field: "productName", headerName: "Product Names", width: 120 },
    { field: "quantity", headerName: "Quantity", width: 120 },
    { field: "amount", headerName: "Amount", width: 100 },
    { field: "deliveryDate", headerName: "Delivery Date", width: 100, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY") : "----", },
    {
      field: "orderStatus",
      headerName: "Order Status",
      width: 150,
      renderCell: (params) => (
        <div className="mt-1">
          <span
            className={`text-white rounded-full px-3 py-1 ${params.value === "DELIVERED"
              ? "bg-green-500"
              : params.value === "SHIPPED"
                ? "bg-purple-500"
                : params.value === "CANCELLED"
                  ? "bg-red-500"
                  : params.value === "PLACED"
                    ? "bg-gray-600"
                    : params.value === "PENDING"
                      ? "bg-yellow-500"
                      : "bg-gray-400"
              }`}
          >
            {params.value}
          </span>
        </div>
      )
    },
    {
      field: "adminPaymentStatus",
      headerName: "Payment Status",
      width: 150,
      renderCell: (params) => (
        <div className="mt-1">
          <span
            className={`text-white rounded-full px-3 py-1 ${params.value === "TRANSFER" ? "bg-green-500" : params.value === "CANCELLED" ? "bg-red-500" : "bg-gray-400"
              }`}
          >
            {params.value === "TRANSFER" ? "RECIEVED" : params.value}
          </span>
        </div>
      )
    }
  ];

  return (
    <div>
      <Box p={2} bgcolor="white" borderRadius={2} boxShadow={1} marginBottom={2}>
        {/* Header Row: Title + Reset Link */}
        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
          <Grid item>
            <Typography variant="subtitle1" fontWeight="bold" color="gray">
              Product Title
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="body2"
              color="error"
              sx={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => {
                setProductTitle("")
              }}
            >
              Reset
            </Typography>
          </Grid>
        </Grid>

        {/* Date Inputs */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <FormControl fullWidth>
              <InputLabel id="product-label">Title</InputLabel>
              <Select
                labelId="product-label"
                label="Title"
                value={productTitle}
                onChange={(e) => setProductTitle(e.target.value)}
              >
                <MenuItem value={""}>All Products</MenuItem>
                {vendorProducts.products?.response?.map((product) => (
                  <MenuItem key={product.id} value={product.title}>
                    {product.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          {/* Order Date Filter Block */}
          <Grid item xs={12} md={6}>
            <Box p={2} bgcolor="white" borderRadius={2} boxShadow={1}>
              {/* Header Row: Title + Reset Link */}
              <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                <Grid item>
                  <Typography variant="subtitle1" fontWeight="bold" color="gray">
                    Order
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                      setOrderDateFrom("");
                      setOrderDateTo("");
                    }}
                  >
                    Reset
                  </Typography>
                </Grid>
              </Grid>

              {/* Date Inputs */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: orderDateTo || new Date().toISOString().split("T")[0],
                      min: "2000-01-01",
                    }}
                    value={orderDateFrom}
                    onChange={(e) => setOrderDateFrom(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: new Date().toISOString().split("T")[0],
                      min: orderDateFrom || "2000-01-01",
                    }}
                    value={orderDateTo}
                    onChange={(e) => setOrderDateTo(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>


          {/* Payment Date Filter Block */}
          <Grid item xs={12} md={6}>
            <Box p={2} bgcolor="white" borderRadius={2} boxShadow={1}>
              {/* Header Row: Title + Reset link */}
              <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                <Grid item>
                  <Typography variant="subtitle1" fontWeight="bold" color="gray">
                    Payment
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                      setPaymentDateFrom("");
                      setPaymentDateTo("");
                    }}
                  >
                    Reset
                  </Typography>
                </Grid>
              </Grid>

              {/* Date Fields */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: paymentDateTo || new Date().toISOString().split("T")[0], // can't go beyond 'To' or today
                      min: "2000-01-01",
                    }}
                    value={paymentDateFrom}
                    onChange={(e) => setPaymentDateFrom(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: new Date().toISOString().split("T")[0], // restrict to today
                      min: paymentDateFrom || "2000-01-01",        // can't go before 'From'
                    }}
                    value={paymentDateTo}
                    onChange={(e) => setPaymentDateTo(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

        </Grid>
      </Box>

      <Card className="mt-2" sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardHeader
          title={
            <Typography variant="h6" className="text-gray-700">
              PAYMENTS
            </Typography>
          }
        />
        <CardContent>
          {/* Buttons and Search Box */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExportCSV}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Print
              </Button>
            </Box>
            {/* Search Box */}
            <Box>
              <TextField
                label="Search Payments"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: { xs: '100%', sm: '300px' } }}
              />
            </Box>
          </Box>
          <Box sx={{ height: "100%", width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[10, 25, 50, 75, 100]}
              getRowId={(row) => row.id}
              checkboxSelection           // ✅ Enable checkbox selection
              onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)} // Track selection
              selectionModel={selectedRowIds} // selected mode
              disableSelectionOnClick
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              loading={vendorPayments.loading}
              autoHeight={true}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap", // prevents wrapping
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default Payments;
